// Debug utility to test Firestore permissions step by step
import { doc, getDoc, setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

export const testFirebaseConnection = async () => {
  try {
    console.log('[DEBUG] Testing Firebase connection...');
    
    // Try to read a simple document that should exist or be readable
    const testRef = doc(db, 'test', 'connection');
    await getDoc(testRef);
    
    console.log('[DEBUG] ✓ Firebase connection working');
    return { success: true, message: 'Firebase connection OK' };
  } catch (error) {
    console.error('[DEBUG] ✗ Firebase connection failed:', error);
    return { success: false, message: 'Firebase connection failed: ' + error.message };
  }
};

export const checkUserType = async (user) => {
  if (!user) {
    console.error('[DEBUG] No user provided for userType check');
    return null;
  }

  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      console.log('[DEBUG] User data from Firestore:', userData);
      return userData.userType || 'customer';
    } else {
      console.log('[DEBUG] User document does not exist in Firestore');
      return 'customer';
    }
  } catch (error) {
    console.error('[DEBUG] Error reading user data:', error);
    return null;
  }
};

export const createUserDocumentIfMissing = async (user) => {
  if (!user) return { success: false, message: 'No user provided' };

  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.log('[DEBUG] User document missing, creating default customer profile...');
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        name: user.name || '',
        userType: 'customer',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('[DEBUG] ✓ Created default user document');
      return { success: true, message: 'Created user document' };
    } else {
      console.log('[DEBUG] ✓ User document already exists');
      return { success: true, message: 'User document exists' };
    }
  } catch (error) {
    console.error('[DEBUG] Error creating user document:', error);
    return { success: false, message: error.message };
  }
};

export const testFirestorePermissions = async (user) => {
  if (!user) {
    console.error('[DEBUG] No user provided for permission test');
    return { success: false, message: 'No user authenticated' };
  }

  console.log('[DEBUG] Testing Firestore permissions for user:', {
    uid: user.uid,
    email: user.email,
    name: user.name
  });

  try {
    // Test 0: Check Firebase connection
    console.log('[DEBUG] Test 0: Checking Firebase connection...');
    const connectionTest = await testFirebaseConnection();
    if (!connectionTest.success) {
      console.error('[DEBUG] ✗ Firebase connection failed:', connectionTest.message);
      return { success: false, message: 'Firebase connection issue: ' + connectionTest.message };
    }
    console.log('[DEBUG] ✓ Firebase connection verified');
    
    // Test 1: Check user authentication token
    console.log('[DEBUG] Test 1: Checking user auth token...');
    const token = await user.getIdToken();
    console.log('[DEBUG] ✓ User has valid auth token, length:', token.length);

    // Test 1: Read user document to check userType
    console.log('[DEBUG] Test 2: Reading user document...');
    const userRef = doc(db, 'users', user.uid);
    try {
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        console.log('[DEBUG] ✓ User document exists:', userData);
      } else {
        console.log('[DEBUG] ⚠ User document does not exist, will use default customer type');
      }
    } catch (err) {
      console.error('[DEBUG] ✗ Cannot read user document:', err.message);
      return { success: false, message: 'Cannot read user profile: ' + err.message };
    }

    // Test 2: Read from store collection
    console.log('[DEBUG] Test 3: Reading from store collection...');
    const testProductRef = doc(db, 'store', 'dhanaura', 'groups', 'test', 'subgroups', 'test', 'products', 'test');
    try {
      await getDoc(testProductRef);
      console.log('[DEBUG] ✓ Store read permission OK');
    } catch (err) {
      console.error('[DEBUG] ✗ Store read permission FAILED:', err.message);
      return { success: false, message: 'Cannot read store data: ' + err.message };
    }

    // Test 3: Write to the main 'orders' collection
    console.log("[DEBUG] Test 4: Writing to 'orders' collection...");
    const testOrderRef = doc(db, 'orders', `test_${user.uid}`);
    try {
      const testOrderData = {
        test: true,
        createdAt: serverTimestamp(),
        userId: user.uid,
        userEmail: user.email,
        status: 'pending', // Must match security rules requirement
        deliveryPersonId: null, // Must be null to match security rules
        items: [],
        total: 0
      };
      console.log('[DEBUG] Attempting to write order data:', testOrderData);
      await setDoc(testOrderRef, testOrderData);
      console.log("[DEBUG] ✓ 'orders' collection write permission OK");
      
      // Immediately clean up the test document so it doesn't pollute the database
      await deleteDoc(testOrderRef);
      console.log("[DEBUG] ✓ Test order document deleted successfully");
    } catch (err) {
      console.error("[DEBUG] ✗ 'orders' collection write permission FAILED:", err.message);
      console.error('[DEBUG] Full error:', err);
      return { success: false, message: "Cannot write to 'orders' collection: " + err.message };
    }

    // Test 4: Write to store orders collection - REMOVED
    // This test has been removed because we no longer use the store/orders subcollection
    
    // Test 5: Read/Write cart data
    console.log('[DEBUG] Test 5: Writing to cart collection...');
    const testCartRef = doc(db, 'incart', user.uid);
    try {
      await setDoc(testCartRef, {
        test: true,
        items: {}
      });
      console.log('[DEBUG] ✓ Cart write permission OK');
    } catch (err) {
      console.error('[DEBUG] ✗ Cart write permission FAILED:', err.message);
      return { success: false, message: 'Cannot write to cart: ' + err.message };
    }

    console.log('[DEBUG] All permission tests passed!');
    return { success: true, message: 'All permissions OK' };

  } catch (generalErr) {
    console.error('[DEBUG] General permission test error:', generalErr);
    return { 
      success: false, 
      message: 'Permission test failed: ' + generalErr.message,
      error: generalErr
    };
  }
};

export const debugUserAuthState = async (user) => {
  if (!user) {
    console.error('[DEBUG] No user provided for auth state check');
    return { authenticated: false, message: 'No user object' };
  }

  try {
    console.log('[DEBUG] User auth state:', {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      isAnonymous: user.isAnonymous,
      providerId: user.providerId,
      name: user.name
    });

    // Test getting ID token
    const token = await user.getIdToken(false);
    console.log('[DEBUG] User has valid token, length:', token.length);
    
    // Parse token claims (basic info)
    const tokenResult = await user.getIdTokenResult();
    console.log('[DEBUG] Token claims:', {
      email: tokenResult.claims.email,
      email_verified: tokenResult.claims.email_verified,
      firebase: tokenResult.claims.firebase
    });

    return { 
      authenticated: true, 
      uid: user.uid,
      email: user.email,
      token: token.substring(0, 50) + '...',
      message: 'User is properly authenticated'
    };
  } catch (error) {
    console.error('[DEBUG] Error checking user auth state:', error);
    return { 
      authenticated: false, 
      message: 'Authentication error: ' + error.message,
      error: error
    };
  }
};